import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";

const NoticeReaders = () => {
  const { noticeId } = useParams();
  const [title, setTitle] = useState("");
  const [readRows, setReadRows] = useState([]);
  const [unreadRows, setUnreadRows] = useState([]);
  const [readDeadlineDate, setReadDeadlineDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [detailRes, readersRes] = await Promise.all([
          api.get(`/notices/${noticeId}/`),
          api.get(`/notices/${noticeId}/readers/`),
        ]);
        setTitle(detailRes.data.title || "");
        const data = readersRes.data;
        if (data && typeof data === "object" && !Array.isArray(data)) {
          setReadRows(Array.isArray(data.read) ? data.read : []);
          setUnreadRows(Array.isArray(data.unread) ? data.unread : []);
          setReadDeadlineDate(data.read_deadline_date || null);
          if (data.title) setTitle(data.title);
        } else if (Array.isArray(data)) {
          setReadRows(data);
          setUnreadRows([]);
          setReadDeadlineDate(null);
        }
      } catch (e) {
        console.error(e);
        setError("データを取得できませんでした。");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [noticeId]);

  const formatDateTime = (dt) => {
    if (!dt) return "-";
    const d = new Date(dt);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const formatDate = (ds) => {
    if (!ds) return "-";
    const s = String(ds).slice(0, 10);
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
  };

  const tableCellStyle = { padding: "12px 20px", verticalAlign: "middle" };
  const tableHeadStyle = { ...tableCellStyle, whiteSpace: "nowrap" };

  const renderTable = (rows, columns, emptyText) => {
    if (rows.length === 0) {
      return (
        <p style={{ textAlign: "center", color: "#9ca3af", padding: "32px 0", margin: 0 }}>
          {emptyText}
        </p>
      );
    }
    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
          <thead>
            <tr style={{ background: "#1e3a5f", color: "#fff" }}>
              {columns.map((c) => (
                <th key={c.key} style={tableHeadStyle}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={`${r.memberId}-${i}`}
                style={{
                  background: i % 2 === 0 ? "#fff" : "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                {columns.map((c) => (
                  <td key={c.key} style={tableCellStyle}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const readColumns = [
    { key: "id", label: "社員コード", render: (r) => r.memberId || "-" },
    {
      key: "name",
      label: "名前",
      render: (r) => <span style={{ fontWeight: 600 }}>{r.name || "-"}</span>,
    },
    { key: "dept", label: "部署・課名", render: (r) => r.departmentName || "-" },
    {
      key: "at",
      label: "確認日時",
      render: (r) => (
        <span style={{ color: "#6b7280", fontSize: "0.88rem" }}>{formatDateTime(r.read_at)}</span>
      ),
    },
  ];

  const unreadColumns = [
    { key: "id", label: "社員コード", render: (r) => r.memberId || "-" },
    {
      key: "name",
      label: "名前",
      render: (r) => <span style={{ fontWeight: 600 }}>{r.name || "-"}</span>,
    },
    { key: "dept", label: "部署・課名", render: (r) => r.departmentName || "-" },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f3f4f6",
        padding: "40px 24px 60px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}>
          <button
            className="btn btn-secondary"
            onClick={() => window.close()}
            style={{ whiteSpace: "nowrap" }}
            type="button"
          >
            ✕ 閉じる
          </button>
          <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700, color: "#111827" }}>
            既読・未読の確認{title ? `：${title}` : ""}
          </h3>
        </div>

        {readDeadlineDate && (
          <div
            style={{
              marginBottom: "20px",
              padding: "12px 16px",
              background: "#fffbeb",
              border: "1px solid #fcd34d",
              borderRadius: 8,
              color: "#92400e",
              fontSize: "0.95rem",
            }}
          >
            <strong>確認締切日：</strong>
            {formatDate(readDeadlineDate)}
            <span style={{ marginLeft: 12, color: "#78350f" }}>
              （締切の前日に、未読の方へアプリ内リマインドを自動送信します）
            </span>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {loading ? (
          <p style={{ textAlign: "center", color: "#6b7280", padding: "48px 0" }}>読み込み中...</p>
        ) : (
          <>
            <section
              style={{
                background: "#fff",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                overflow: "hidden",
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  padding: "14px 24px",
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    background: "#fef2f2",
                    color: "#b91c1c",
                    border: "1px solid #fecaca",
                    borderRadius: "12px",
                    padding: "3px 14px",
                    fontSize: "0.88rem",
                    fontWeight: 600,
                  }}
                >
                  まだ読んでいない人 {unreadRows.length} 名
                </span>
              </div>
              {renderTable(unreadRows, unreadColumns, "未読の方はいません（全員確認済み、または対象外です）。")}
            </section>

            <section
              style={{
                background: "#fff",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                overflow: "hidden",
              }}
            >
              <div style={{ padding: "14px 24px", borderBottom: "1px solid #e5e7eb" }}>
                <span
                  style={{
                    display: "inline-block",
                    background: "#f0fdf4",
                    color: "#166534",
                    border: "1px solid #86efac",
                    borderRadius: "12px",
                    padding: "3px 14px",
                    fontSize: "0.88rem",
                    fontWeight: 600,
                  }}
                >
                  既に確認した人 {readRows.length} 名
                </span>
              </div>
              {renderTable(readRows, readColumns, "既読の記録はまだありません。")}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default NoticeReaders;
